import { criterion, fromCriteria, unsupported } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readBerechnungsmodus } from "@/office/excel/analyse";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { MAKRO_ANSICHT } from "../makros";

export const lektionBerechnung: Lesson = {
  id: "berechnung",
  title: "Berechnung und Formelüberwachung",
  summary: "Wann Excel rechnet, und wie man einer fremden Formel auf die Spur kommt.",
  durationMinutes: 10,
  learningGoals: [
    "Die Berechnungsoptionen kennen und richtig einstellen",
    "Mit Spur zum Vorgänger und Nachfolger Abhängigkeiten sichtbar machen",
    "Wichtige Zellen im Überwachungsfenster beobachten",
  ],
  steps: [
    {
      id: "berechnungsoptionen",
      title: "Berechnungsoptionen",
      explanation: [
        "„Formeln“ → „Berechnungsoptionen“ legt fest, wann Excel rechnet: „Automatisch“ " +
          "nach jeder Änderung, „Automatisch außer bei Datentabellen“ oder „Manuell“ – dann " +
          "erst mit F9.",
        "„Manuell“ ist eine berüchtigte Fehlerquelle: Die Einstellung gilt für alle " +
          "geöffneten Mappen und wird mit einer Mappe gespeichert. Wer eine solche Datei " +
          "öffnet, sieht veraltete Ergebnisse, ohne es zu merken – bis auf das unscheinbare " +
          "„Berechnen“ in der Statusleiste.",
      ],
      keyPoints: [
        "Formeln → Berechnungsoptionen → Automatisch.",
        "„Manuell“ gilt für alle offenen Mappen – F9 rechnet neu.",
        "„Berechnen“ in der Statusleiste = es steht etwas aus.",
      ],
      task: {
        instruction:
          "Stelle die Berechnung auf „Automatisch“ oder „Automatisch außer bei Datentabellen“.",
        hint: "Formeln → Berechnungsoptionen.",
        check: async () => {
          const modus = await readBerechnungsmodus();
          if (modus === null) {
            return unsupported(
              "Diese Excel-Version meldet den Berechnungsmodus nicht.",
              "Sieh unter Formeln → Berechnungsoptionen nach. Danach kannst du den Schritt selbst als erledigt markieren.",
            );
          }
          return fromCriteria(
            [criterion(`Berechnung automatisch (aktuell: ${modus})`, modus === "Automatic" || modus === "AutomaticExceptTables")],
            {
              success: "Excel rechnet nach jeder Änderung – keine veralteten Ergebnisse.",
              failure: "Die Berechnung steht auf „Manuell“.",
            },
          );
        },
      },
    },
    {
      id: "spuren",
      title: "Spur zum Vorgänger und Nachfolger",
      explanation: [
        "„Formeln“ → „Spur zum Vorgänger“ zeichnet blaue Pfeile von allen Zellen, die in " +
          "die markierte Formel eingehen. „Spur zum Nachfolger“ zeigt, welche Formeln " +
          "diese Zelle verwenden – bevor man eine Zelle löscht, eine wichtige Frage. " +
          "„Pfeile entfernen“ räumt auf.",
        "„Formeln anzeigen“ (Strg + #) zeigt statt der Ergebnisse die Formeln – der " +
          "schnellste Überblick über ein fremdes Blatt, wie im Modul „Formeln und Bezüge“.",
      ],
      keyPoints: [
        "Spur zum Vorgänger: Woher kommen die Werte?",
        "Spur zum Nachfolger: Wer braucht diese Zelle?",
        "Strg + # zeigt alle Formeln.",
      ],
    },
    {
      id: "ueberwachungsfenster",
      title: "Das Überwachungsfenster",
      explanation: [
        "„Formeln“ → „Überwachungsfenster“ → „Überwachung hinzufügen“ hält eine Zelle im " +
          "Blick, egal auf welchem Blatt man gerade arbeitet: Die Liste zeigt Blatt, " +
          "Adresse, aktuellen Wert und Formel.",
        "Typischer Einsatz: das Endergebnis einer Kalkulation beobachten, während man auf " +
          "einem anderen Blatt Eingaben ändert.",
      ],
      keyPoints: [
        "Formeln → Überwachungsfenster → Überwachung hinzufügen.",
        "Wert und Formel bleiben beim Blattwechsel sichtbar.",
      ],
      task: {
        instruction: "Füge dem Überwachungsfenster mindestens eine Zelle mit einer Formel hinzu.",
        hint: "Zelle markieren, Formeln → Überwachungsfenster → Überwachung hinzufügen.",
        macro: MAKRO_ANSICHT,
        check: () =>
          pruefeMitMakro(MAKRO_ANSICHT, (bericht) =>
            fromCriteria(
              [criterion(`Überwachte Zellen: ${bericht.zahl("ueberwachungen") ?? 0}`, (bericht.zahl("ueberwachungen") ?? 0) > 0)],
              {
                success: "Die wichtige Zelle bleibt im Blick, egal wo du arbeitest.",
                failure: "Das Überwachungsfenster ist noch leer.",
              },
            ),
          ),
      },
    },
  ],
};
